import { useEffect, useState } from "react";
import styles from "./App.module.css";
import MobileBlock from "./components/MobileBlock";
import Sidebar from "./components/Sidebar";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import UpdatePasswordPage from "./pages/UpdatePasswordPage";
import OverviewPage from "./pages/OverviewPage";
import AccountsPage from "./pages/AccountsPage";
import SupportPage from "./pages/SupportPage";
import SupportTicketPage from "./pages/SupportTicketPage";
import ReferralsPage from "./pages/ReferralsPage";
import PayoutsPage from "./pages/PayoutsPage";
import FinancialsPage from "./pages/FinancialsPage";
import RiskDeskPage from "./pages/RiskDeskPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import TeamPage from "./pages/TeamPage";
import { NAV_ITEMS, NAV_ROUTES } from "./navItems";
import useIsMobile from "./useIsMobile";
import { ADMIN_ROLES, UNAUTHORIZED_EVENT, apiRequest, readToken, storeToken, toSessionUser } from "./api";

const AUTH_PATHS = ["/login", "/forgot-password", "/update-password"];
const PORTAL_PATHS = NAV_ROUTES.map((item) => item.path);
const TICKET_PATH = /^\/support\/([\w-]+)$/;

function normalizePath(pathname) {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  if (AUTH_PATHS.includes(trimmed) || PORTAL_PATHS.includes(trimmed) || TICKET_PATH.test(trimmed)) {
    return trimmed;
  }
  if (trimmed === "/withdrawals") {
    return "/withdrawals/payouts";
  }
  return "/overview";
}

function usePortalRoute() {
  const [pathname, setPathname] = useState(() => normalizePath(window.location.pathname));

  useEffect(() => {
    const handlePopState = () => {
      setPathname(normalizePath(window.location.pathname));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (nextPath) => {
    const normalized = normalizePath(nextPath);

    if (normalized === pathname) {
      return;
    }

    window.history.pushState({}, "", normalized);
    window.scrollTo(0, 0);
    setPathname(normalized);
  };

  return { pathname, navigate };
}

export default function App() {
  const { pathname, navigate } = usePortalRoute();
  const isMobile = useIsMobile();
  const [user, setUser] = useState(null);
  // A stored token is checked against /api/auth/me before the portal renders
  const [checkingSession, setCheckingSession] = useState(() => Boolean(readToken()));

  const isAuthPath = AUTH_PATHS.includes(pathname);

  useEffect(() => {
    if (!checkingSession) {
      return undefined;
    }
    const controller = new AbortController();
    apiRequest("/api/auth/me", { signal: controller.signal })
      .then(({ user: apiUser }) => {
        if (ADMIN_ROLES.includes(apiUser.role)) {
          setUser(toSessionUser(apiUser));
        } else {
          storeToken(null);
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        // Keep the token on a network blip; drop it if the API rejected it
        if (err.status) storeToken(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setCheckingSession(false);
      });
    return () => controller.abort();
  }, [checkingSession]);

  // Any API call answered 401 means the token expired or was revoked
  useEffect(() => {
    const handleUnauthorized = () => {
      storeToken(null);
      setUser(null);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  // Signed-out users can only reach the auth screens
  useEffect(() => {
    if (!checkingSession && !user && !isAuthPath) {
      navigate("/login");
    }
  });

  useEffect(() => {
    const ticketMatch = pathname.match(TICKET_PATH);
    const item = NAV_ROUTES.find((navItem) => navItem.path === pathname);
    const label = ticketMatch ? `Ticket ${ticketMatch[1]}` : item?.label;
    document.title = label ? `${label} · PropFunding Admin` : "PropFunding Admin";
  }, [pathname]);

  const startSession = (token, apiUser) => {
    storeToken(token);
    setUser(toSessionUser(apiUser));
    navigate("/overview");
  };

  // Resolves to { error } so the login form can show why it failed
  const handleLogin = async ({ email, password }) => {
    try {
      const { token, user: apiUser } = await apiRequest("/api/auth/login", {
        method: "POST",
        body: { email, password },
      });
      if (!ADMIN_ROLES.includes(apiUser.role)) {
        return { error: "This account doesn't have admin access." };
      }
      startSession(token, apiUser);
      return {};
    } catch (err) {
      return { error: err.message };
    }
  };

  const handleLogout = () => {
    storeToken(null);
    setUser(null);
    navigate("/login");
  };

  // The admin portal is desktop-only; phones get a holding screen instead
  if (isMobile) {
    return <MobileBlock />;
  }

  if (checkingSession) {
    return null;
  }
  if (pathname === "/login") {
    return <LoginPage onLogin={handleLogin} onNavigate={navigate} />;
  }
  if (pathname === "/forgot-password") {
    return <ForgotPasswordPage onNavigate={navigate} />;
  }
  if (pathname === "/update-password") {
    return <UpdatePasswordPage onNavigate={navigate} onAuthenticated={startSession} />;
  }
  if (!user) {
    return null;
  }

  const ticketMatch = pathname.match(TICKET_PATH);

  return (
    <div className={styles.shell}>
      <Sidebar
        activePath={pathname}
        navItems={NAV_ITEMS}
        onNavigate={navigate}
        user={user}
        onLogout={handleLogout}
      />
      <main className={styles.main}>
        {pathname === "/overview" ? (
          <OverviewPage onNavigate={navigate} />
        ) : pathname === "/accounts" ? (
          <AccountsPage />
        ) : pathname === "/withdrawals/payouts" ? (
          <PayoutsPage />
        ) : pathname === "/withdrawals/rewards" ? (
          <ReferralsPage />
        ) : pathname === "/financials" ? (
          <FinancialsPage />
        ) : pathname === "/risk" ? (
          <RiskDeskPage />
        ) : pathname === "/leaderboard" ? (
          <LeaderboardPage />
        ) : pathname === "/team" ? (
          <TeamPage user={user} />
        ) : pathname === "/support" ? (
          <SupportPage onNavigate={navigate} />
        ) : ticketMatch ? (
          <SupportTicketPage key={ticketMatch[1]} ticketId={ticketMatch[1]} onNavigate={navigate} />
        ) : null}
      </main>
    </div>
  );
}
